import { useEffect, useRef } from "react";
import {
    useQuery,
    useMutation,
    QueryFunction,
    UseQueryOptions,
    QueryKey,
    UseMutationOptions,
    MutationKey,
    MutationFunction,
    UseMutationResult,
    MutateOptions,
    useInfiniteQuery,
    UseInfiniteQueryOptions,
    keepPreviousData as keepPreviousDataPlaceholder,
    QueryClient,
    InfiniteData,
    UseInfiniteQueryResult
} from "@tanstack/react-query";

type genericError = {
    statusCode: number;
};

type Require<T, K extends keyof T> = T & { [P in K]-?: T[P] };

type LegacyQueryCallbacks<TData, TError> = {
    cacheTime?: number;
    keepPreviousData?: boolean;
    onSuccess?: (data: TData) => void;
    onError?: (error: TError) => void;
};

type LegacyInfiniteQueryCallbacks<TData, TError> = {
    cacheTime?: number;
    keepPreviousData?: boolean;
    onSuccess?: (data: InfiniteData<TData>) => void;
    onError?: (error: TError) => void;
};

type OptionType<TQueryFnData, TError, TData, TQueryKey extends QueryKey = QueryKey> = Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    "queryKey" | "queryFn"
> &
    LegacyQueryCallbacks<TData, TError>;
type NewOptionType<TQueryFnData, TError, TData, TQueryKey extends QueryKey = QueryKey> = Require<
    OptionType<TQueryFnData, TError, TData, TQueryKey>,
    "onSuccess" | "onError"
>;

export type ClientHookOptions<TQueryFnData, ErrorType = unknown, TData = TQueryFnData> = Omit<
    Require<
        OptionType<TQueryFnData, ErrorType extends unknown ? genericError : ErrorType, TData>,
        "onSuccess" | "onError"
    >,
    "select"
>;

function useLegacyQueryCallbacks<TData, TError>(
    isSuccess: boolean,
    isError: boolean,
    data: TData | undefined,
    error: TError | null,
    onSuccess?: (data: TData) => void,
    onError?: (error: TError) => void
) {
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;

    const lastSuccessData = useRef<TData | undefined>(undefined);
    const lastError = useRef<TError | undefined>(undefined);

    useEffect(() => {
        if (isSuccess && data !== undefined && data !== lastSuccessData.current) {
            lastSuccessData.current = data;
            onSuccessRef.current?.(data);
        }
    }, [isSuccess, data]);

    useEffect(() => {
        if (isError && error && error !== lastError.current) {
            lastError.current = error;
            onErrorRef.current?.(error);
        }
    }, [isError, error]);
}

function mapLegacyQueryOptions<T extends LegacyQueryCallbacks<any, any>>(options: T) {
    const { cacheTime, keepPreviousData, onSuccess, onError, ...rest } = options;
    return {
        rest,
        onSuccess,
        onError,
        mapped: {
            ...rest,
            gcTime: cacheTime ?? (rest as { gcTime?: number }).gcTime,
            placeholderData: keepPreviousData ? keepPreviousDataPlaceholder : (rest as { placeholderData?: unknown }).placeholderData
        }
    };
}

export function useVahakQuery<TQueryFnData, TError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
    queryKey: TQueryKey | string,
    queryFn:
        | QueryFunction<TQueryFnData, TQueryKey>
        | ((...args: any[]) => TQueryFnData | Promise<TQueryFnData>),
    options: NewOptionType<TQueryFnData, TError extends unknown ? genericError : TError, TData, TQueryKey>
) {
    const { mapped, onSuccess, onError } = mapLegacyQueryOptions(options);
    const result = useQuery<TQueryFnData, TError extends unknown ? genericError : TError, TData, TQueryKey>({
        queryKey: queryKey as TQueryKey,
        queryFn: queryFn as QueryFunction<TQueryFnData, TQueryKey>,
        ...mapped
    });
    useLegacyQueryCallbacks(result.isSuccess, result.isError, result.data, result.error, onSuccess, onError);
    return result;
}

type Modify<T, R> = Omit<T, keyof R> & R;

type _UseMutationResult<TData, TError, TVariables, TContext> = UseMutationResult<TData, TError, TVariables, TContext>;

type ModifiedMutationResultType<TData, TError, TVariables, TContext> = Modify<
    _UseMutationResult<TData, TError, TVariables, TContext>,
    {
        mutate: NewMutateTypeWithOnSuccessAndOnErrorMandatory<TData, TError, TVariables, TContext>;
        isLoading: boolean;
    }
>;

type NewMutateTypeWithOnSuccessAndOnErrorMandatory<TData, TError, TVariables, TContext> = (
    variables: TVariables,
    options: NewMutateOptionType<TData, TError, TVariables, TContext>
) => void;

type NewMutateOptionType<TData, TError, TVariables, TContext> = Require<
    MutateOptions<TData, TError, TVariables, TContext>,
    "onSuccess" | "onError"
>;

export function useVahakMutationQuery<TData, TError, TVariables, TContext = unknown>(
    mutationKey: MutationKey | string,
    mutationFn: MutationFunction<TData, TVariables>,
    options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, "mutationKey" | "mutationFn">
): ModifiedMutationResultType<TData, TError, TVariables, TContext> {
    const result = useMutation<TData, TError, TVariables, TContext>({
        mutationKey: mutationKey as MutationKey,
        mutationFn,
        ...options
    });
    return {
        ...result,
        isLoading: result.isPending
    } as ModifiedMutationResultType<TData, TError, TVariables, TContext>;
}

type InfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey extends QueryKey = QueryKey> = Omit<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, unknown>,
    "queryKey" | "queryFn" | "initialPageParam"
> &
    LegacyInfiniteQueryCallbacks<TData, TError> & { initialPageParam?: unknown };
type NewInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey
> = Require<InfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey>, "onSuccess" | "onError" | "getNextPageParam">;

export type ClientHookInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TAggregatedData,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey
> = Modify<
    Omit<NewInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey>, "getNextPageParam">,
    {
        onSuccess: (data: TAggregatedData[]) => void;
    }
>;

export function useVahakInfiniteQuery<
    TQueryFnData,
    TError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey
>(
    queryKey: TQueryKey | string,
    queryFn: QueryFunction<TQueryFnData, TQueryKey, any>,
    options: NewInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseInfiniteQueryResult<InfiniteData<TData>, TError> & { isLoading: boolean } {
    const { mapped, onSuccess, onError } = mapLegacyQueryOptions(options);
    const result = useInfiniteQuery({
        queryKey: queryKey as TQueryKey,
        queryFn,
        ...mapped,
        initialPageParam: (mapped as { initialPageParam?: unknown }).initialPageParam ?? 1
    } as any) as UseInfiniteQueryResult<InfiniteData<TData>, TError>;
    useLegacyQueryCallbacks(result.isSuccess, result.isError, result.data, result.error, onSuccess, onError);
    return {
        ...result,
        isLoading: result.isPending && result.isFetching
    } as UseInfiniteQueryResult<InfiniteData<TData>, TError> & { isLoading: boolean };
}

function toQueryKey(queryKey: unknown) {
    return Array.isArray(queryKey) ? queryKey : [queryKey];
}

function mapLegacyQueryFilters(queryKeyOrFilters?: any, extra?: any) {
    if (queryKeyOrFilters == null) {
        return extra ? [extra] : [];
    }
    if (typeof queryKeyOrFilters === "string" || Array.isArray(queryKeyOrFilters)) {
        const { inactive, active, throwOnError, cancelRefetch, ...rest } = extra || {};
        const filters: Record<string, unknown> = {
            queryKey: toQueryKey(queryKeyOrFilters),
            ...rest
        };
        if (inactive === true) filters.type = "inactive";
        if (active === true) filters.type = "active";
        const options =
            throwOnError !== undefined || cancelRefetch !== undefined ? { throwOnError, cancelRefetch } : undefined;
        return options ? [filters, options] : [filters];
    }
    return extra !== undefined ? [queryKeyOrFilters, extra] : [queryKeyOrFilters];
}

export function createVahakQueryClient() {
    const client = new QueryClient();
    const originalInvalidate = client.invalidateQueries.bind(client);
    const originalRefetch = client.refetchQueries.bind(client);
    const originalCancel = client.cancelQueries.bind(client);
    const originalRemove = client.removeQueries.bind(client);

    client.invalidateQueries = ((queryKeyOrFilters?: any, extra?: any) =>
        (originalInvalidate as (...args: any[]) => Promise<void>)(...mapLegacyQueryFilters(queryKeyOrFilters, extra))) as typeof client.invalidateQueries;
    client.refetchQueries = ((queryKeyOrFilters?: any, extra?: any) =>
        (originalRefetch as (...args: any[]) => Promise<void>)(...mapLegacyQueryFilters(queryKeyOrFilters, extra))) as typeof client.refetchQueries;
    client.cancelQueries = ((queryKeyOrFilters?: any, extra?: any) =>
        (originalCancel as (...args: any[]) => Promise<void>)(...mapLegacyQueryFilters(queryKeyOrFilters, extra))) as typeof client.cancelQueries;
    client.removeQueries = ((queryKeyOrFilters?: any, extra?: any) =>
        (originalRemove as (...args: any[]) => Promise<void>)(...mapLegacyQueryFilters(queryKeyOrFilters, extra))) as typeof client.removeQueries;

    return client;
}
